/*
 * IMPORTS
 */

import React from 'react' // Npm: react.js library.
import {
  Box,
  Flex,
  Text,
  Progress,
  Spinner,
  useStyleConfig,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.
import { FaHdd } from 'react-icons/fa' // Npm: React icons library.
import { useSelector } from 'react-redux' // Npm: Redux selector hook.


/*
 * COMPONENT
 */
const Index = ({ title }) => {
  // Const assignment.
  const _styles = useStyleConfig('Card')
  const _isCurrentViewMobile = useBreakpointValue(
    { 'base': true, 'sm': true, 'md': false, 'lg': false, 'xl': false },
    { 'fallback': 'base' }
  )

  // Get data from Redux store instead of subscription.
  const { data: dbServerData, error: dbServerError, loading: isLoading } = useSelector(
    (state) => state.SystemInfo.remote
  )

  // Extract disk info.
  const _diskInfo = dbServerData?.diskInfo || {}

  // Calculate percentage and color.
  const parseSize = (sizeStr) => {
    if (!sizeStr) return 0
    const match = String(sizeStr).match(/^([\d.]+)([KMGT])$/)
    if (!match) return parseFloat(sizeStr) || 0

    const value = parseFloat(match[1])
    const unit = match[2]
    const multipliers = { 'K': 1, 'M': 1024, 'G': 1024 * 1024, 'T': 1024 * 1024 * 1024 }
    return value * (multipliers[unit] || 1)
  }

  const totalSize = parseSize(_diskInfo?.total)
  const usedSize = parseSize(_diskInfo?.used)
  const percent = totalSize > 0 ? (usedSize / totalSize) * 100 : 0
  const color = percent > 80 ? 'red' : percent > 60 ? 'orange' : 'blue'

  // Return component.
  return (
    <Box
      __css={_styles}
      borderRadius="20px"
      boxShadow="14px 17px 40px 4px rgba(112,144,176,0.17)"
      p={_isCurrentViewMobile ? 4 : 6}
      bgGradient="linear-gradient(23deg, #FFDBE7 0%, #BCC0FF 99.80%)"
      minH="220px"
      w="100%"
    >
      {/* Header */}
      <Flex
        mb={_isCurrentViewMobile ? 4 : 5}
        align={_isCurrentViewMobile ? 'flex-start' : 'center'}
        flexDir={_isCurrentViewMobile ? 'column' : 'row'}
      >
        <FaHdd
          size={_isCurrentViewMobile ? 22 : 26}
          color='rgba(43,54,116,1)'
          style={{ 'marginRight': 8 }}
        />
        <Text
          color='rgba(43,54,116,1)'
          fontWeight="700"
          fontSize="18px"
        >
          {title}
        </Text>
      </Flex>

      {/* Error */}
      {dbServerError && (
        <Text color="red.500" textAlign="center">
          {dbServerError.message}
        </Text>
      )}

      {/* Used / Total */}
      <Text fontSize="16px" fontWeight="800" color="#2B3674">
        <Text as="span" fontWeight="500" mr="2">
          (Used / Total)
        </Text>

        {isLoading ? (
          <Spinner size="sm" color="blue.400" />
        ) : (
          <Text as="span" fontWeight="800" color="#2B3674">
            {_diskInfo?.used || '0G'} / {_diskInfo?.total || '0T'}
          </Text>
        )}
      </Text>

      {/* Available */}
      <Text fontSize="16px" mt="2" color="#2B3674">
        Available:{' '}
        {isLoading ? (
          <Spinner size="sm" color="blue.400" />
        ) : (
          <Text as="span" fontWeight="700" color="#2B3674">
            {_diskInfo?.available || '0T'}
          </Text>
        )}
      </Text>

      {/* Progress */}
      <Box mt={4} w="100%">
        <Progress
          value={isLoading ? 0 : percent}
          colorScheme={color}
          borderRadius="full"
          height="18px"
          w="100%"
        />

        <Flex mt={2} justify="space-between" w="100%">
          <Text
            fontSize="sm"
            fontWeight="bold"
            color={percent > 70 ? 'red.500' : '#2B3674'}
          >
            {percent.toFixed(1)}% Used
          </Text>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color={percent > 70 ? 'red.400' : 'green.500'}
          >
            {(100 - percent).toFixed(1)}% Free
          </Text>
        </Flex>
      </Box>
    </Box>
  )
}


/*
 * EXPORTS
 */
export default Index
